import find from 'lodash/find';
import get from 'lodash/get';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import ConditionRow from './ConditionRow';
import {
  CurrencyType,
  AppPlatformDataAttributeCurrencyCondition as AppPlatformDataAttributeCurrencyConditionType,
} from 'src/actions/rules/condition';
import NumericConditionSection from '../shared/NumericConditionSection';
import { StyledConditionLabel, StyledDropdown } from '../shared/StyledComponents';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import useAppPlatformDataAttributes from '../hooks/useAppPlatformDataAttributes';

const nonConversionCurrencies = [CurrencyType.JPY, CurrencyType.KRW];

type AppPlatformDataCurrencyConditionProps = {
  condition: AppPlatformDataAttributeCurrencyConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const AppPlatformDataAttributeCurrencyCondition: FC<AppPlatformDataCurrencyConditionProps> = ({
  condition,
  index,
  isReadOnly,
  onDelete,
  onUpdate,
}) => {
  const onCurrencyChange = useCallback(
    val => {
      condition.setCurrency(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );
  const limitDecimals = nonConversionCurrencies.indexOf(condition.currency) === -1 ? 2 : 0;

  const appPlatformDataAttributes = useAppPlatformDataAttributes({ fetch: false });
  const label = get(find(appPlatformDataAttributes, { attrId: condition.attrId }), 'label');

  if (isReadOnly) {
    return (
      <>
        <StyledRow index={index} isReadOnly onDelete={onDelete}>
          <SelectorRow>
            <StyledConditionLabel disabled data-aid="readOnlyCurrencyConditionLabel">
              {label}
            </StyledConditionLabel>
            <NumericConditionSection
              isReadOnly
              condition={condition}
              index={index}
              limitDecimals={limitDecimals}
              onUpdate={onUpdate}
            />
            <span data-aid="readOnlyCurrencyTypeLabel">{condition.currency}</span>
          </SelectorRow>
        </StyledRow>
      </>
    );
  }

  const currencyOptions = Object.keys(CurrencyType).map(key => {
    return { value: key, label: CurrencyType[key] };
  });

  return (
    <StyledRow index={index} onDelete={onDelete}>
      <Wrapper>
        <SelectorRow>
          <Tooltip message={label}>
            <ConditionLabel disabled data-aid="currencyConditionLabel">
              {label}
            </ConditionLabel>
          </Tooltip>
          <NumericConditionSection
            condition={condition}
            index={index}
            limitDecimals={limitDecimals}
            onUpdate={onUpdate}
          />
        </SelectorRow>
        <StyledDropdown
          data-aid="currencyConditionCurrencyDropdown"
          searchable
          onSelect={onCurrencyChange}
          options={currencyOptions}
          value={condition.currency}
          placeholder="Select currency type"
        />
      </Wrapper>
    </StyledRow>
  );
};

const ConditionLabel = styled(StyledConditionLabel)`
  margin-right: 6px;
  max-width: 160px;
`;

const SelectorRow = styled.div`
  align-items: center;
  display: flex;
  padding-bottom: 12px;
`;

const StyledRow = styled(ConditionRow)`
  align-items: baseline;
`;

const Wrapper = styled.div`
  width: 100%;
`;

export default AppPlatformDataAttributeCurrencyCondition;
